import React, { useEffect, useMemo, useState } from "react"
import Loader from "./Loader"
import PermittedComponent from "PermittedComponent"
import { Container, Row } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { axiosbaseurl } from "config"
import { FaFilter } from "react-icons/fa"
import { usePermissionData } from "AppContext"
import viewImg from "../assets/images/EditImg.jpg"
import moment from "moment"

const TradingData = () => {
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState("")
  const [selectedYear, setSelectedYear] = useState("")
  const [tradingData, setTradingData] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getCustomers()
  }, [])

  async function getCustomers() {
    setLoading(true)
    try {
      const response = await axiosbaseurl.get(`customer-management`)
      if (response?.data?.success) {
        setCustomers(response?.data?.data)
      }
    } catch (e) {
      console.log("Error", e.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  const { checkAccess } = usePermissionData()

  const onSelectCustomer = async () => {
    setLoading(true)
    try {
      const response = await axiosbaseurl.post(
        `dashboard/stats/portfolio/data`,
        {
          customerId: selectedCustomer,
        }
      )
      if (response?.data?.success) {
        setTradingData(response?.data?.data?.derivatives || [])
      }
    } catch (e) {
      console.log("Error", e.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  // ✅ Month-wise summary (always 12 months)
  const monthlySummary = useMemo(() => {
    if (!tradingData || tradingData.length === 0) {
      // return empty months if no data at all
      const year = selectedYear || new Date().getFullYear().toString()
      return Array.from({ length: 12 }, (_, i) => ({
        month: moment().month(i).year(year).format("MMMM YYYY"),
        invested: null,
        sell: null,
        pl: null,
        hasData: false,
      }))
    }

    const filteredData = selectedYear
      ? tradingData.filter(trade => {
          const date = moment(trade.Sell_Date, "DD/MM/YYYY")
          return date.isValid() && date.year().toString() === selectedYear
        })
      : tradingData

    const summary = {}

    filteredData.forEach(trade => {
      const date = moment(trade.Sell_Date, "DD/MM/YYYY")
      if (!date.isValid()) return

      const key = date.format("MMMM YYYY")
      if (!summary[key]) {
        summary[key] = { invested: 0, sell: 0, pl: 0 }
      }

      summary[key].invested += parseFloat(trade.Buy_Amount) || 0
      summary[key].sell += parseFloat(trade.Sell_Amount) || 0
      summary[key].pl += parseFloat(trade.Book_PL) || 0
    })

    // ✅ Ensure all 12 months are present
    const year = selectedYear || new Date().getFullYear().toString()

    return Array.from({ length: 12 }, (_, i) => {
      const monthKey = moment().month(i).year(year).format("MMMM YYYY")
      if (summary[monthKey]) {
        return { month: monthKey, ...summary[monthKey], hasData: true }
      }
      return { month: monthKey, invested: null, sell: null, pl: null, hasData: false }
    })
  }, [tradingData, selectedYear])

  // ✅ Grand Totals
  const totals = useMemo(() => {
    const withData = monthlySummary.filter(m => m.hasData)
    return withData.reduce(
      (acc, m) => {
        acc.invested += m.invested
        acc.sell += m.sell
        acc.pl += m.pl
        return acc
      },
      { invested: 0, sell: 0, pl: 0 }
    )
  }, [monthlySummary])

  return (
    <React.Fragment>
      {loading && <Loader />}
      <PermittedComponent isAccess={checkAccess(1, 1)}>
        <div className="page-content">
          <Container fluid>
            <Breadcrumb title={""} breadcrumbItem={"Trading"} />
            <div className="card p-3">
              {/* Filters */}
              <div className="d-flex justify-content-end gap-3 mb-3">
                <select
                  className="form-select rounded-5 w-25 h-100"
                  value={selectedCustomer}
                  onChange={e => setSelectedCustomer(e.target.value)}
                >
                  <option>Select Customer</option>
                  {customers?.map(item => (
                    <option key={item?.id} value={item?.id}>
                      {item?.customerName}
                    </option>
                  ))}
                </select>

                <select
                  className="form-select rounded-5 h-100"
                  style={{ width: "15%" }}
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                >
                  <option value="">Select Year</option>
                  {Array.from(
                    { length: new Date().getFullYear() - 2020 + 1 },
                    (_, i) => 2020 + i
                  ).map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <button
                  className="btn Edit-Button"
                  onClick={() => onSelectCustomer()}
                >
                  <FaFilter />
                </button>
              </div>

              {/* Summary Table */}
              {(selectedCustomer && tradingData?.length > 0) ? (
                <div className="table-responsive mt-2">
                  <table className="mb-0 table Custom-Table">
                    <thead>
                      <tr>
                        <th>Sr No.</th>
                        <th>Month</th>
                        <th>Invested Value</th>
                        <th>Sell Value</th>
                        <th>Booked P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlySummary.map((m, idx) => (
                        <tr
                          key={idx}
                          style={{ background: !m.hasData ? "#f9f9f9" : "transparent" }}
                        >
                          <td>{idx + 1}</td>
                          <td>{m.month}</td>
                          <td>{m.hasData ? m.invested.toFixed(2) : "--"}</td>
                          <td>{m.hasData ? m.sell.toFixed(2) : "--"}</td>
                          <td
                            style={{
                              color: m.hasData ? (m.pl >= 0 ? "green" : "red") : "inherit",
                            }}
                          >
                            {m.hasData ? m.pl.toFixed(2) : "--"}
                          </td>
                        </tr>
                      ))}

                      {/* Grand Totals */}
                      <tr className="fw-bold bg-light">
                        <td colSpan={2} className="text-end">
                          Total:
                        </td>
                        <td>
                          {totals.invested > 0 ? totals.invested.toFixed(2) : "--"}
                        </td>
                        <td>{totals.sell > 0 ? totals.sell.toFixed(2) : "--"}</td>
                        <td
                          style={{
                            color:
                              totals.pl > 0 ? "green" : totals.pl < 0 ? "red" : "inherit",
                          }}
                        >
                          {totals.pl !== 0 ? totals.pl.toFixed(2) : "--"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="card p-4 h-100">
                  <Row className="justify-content-center">
                    <div className="col-md-4 d-flex flex-column align-items-center justify-content-center">
                      <img src={viewImg} alt="No data" width={400} />
                      <p className="text-purple fw-bold text-center lh-base">
                        📊🤵 All Customers trading data is now available <br />
                        Use filters to efficiently search and analyze customer
                        wise trades. 📂📈
                      </p>
                    </div>
                  </Row>
                </div>
              )}
            </div>
          </Container>
        </div>
      </PermittedComponent>
    </React.Fragment>
  )
}

export default TradingData
